import React, { memo, useMemo, useCallback } from 'react';
import ReactMapboxGL from 'react-mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supported } from 'mapbox-gl';
import { debounce } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setMapBounds, setZoomLevel } from '../store/reducers/map';
import { DOES_NOT_SUPPORT_MAPBOX } from '../constants/strings';

const accessToken = process.env.REACT_APP_MAPBOX_API_KEY;

const styles = {
  position: 'relative',
  height: '100%',
  width: '100%',
};

const Map = ReactMapboxGL({
  accessToken,
});

const Mapbox = ({ children, handleOnLoad, coordinates, zoom, ...rest }) => {
  const dispatch = useDispatch();

  const mapStyle = {
    style: 'mapbox://styles/mapbox/streets-v11?optimize=true',
  };

  const isSupported = useMemo(() => supported(), []);

  const changeZoom = useCallback(
    (zoom) => {
      dispatch(setZoomLevel(zoom));
    },
    [dispatch]
  );

  const debouncedZoomHandler = useMemo(
    () => debounce(changeZoom, 300),
    [changeZoom]
  );

  const onMapMoved = useCallback(
    (map) => {
      const bounds = map?.getBounds().toArray().flat();
      dispatch(setMapBounds(bounds));
    },
    [dispatch]
  );

  const debouncedMoveHandler = useMemo(
    () => debounce(onMapMoved, 300),
    [onMapMoved]
  );

  return isSupported ? (
    <Map
      onStyleLoad={handleOnLoad}
      center={coordinates}
      {...mapStyle}
      containerStyle={styles}
      zoom={zoom}
      onZoom={(map) => {
        debouncedZoomHandler([map.getZoom()]);
      }}
      onMoveEnd={(map) => {
        debouncedMoveHandler(map);
      }}
      renderChildrenInPortal={true}
      {...rest}
    >
      {children}
    </Map>
  ) : (
    <p>{DOES_NOT_SUPPORT_MAPBOX}</p>
  );
};

export default memo(Mapbox);
